import { buildRpaDomainSeoBundle } from "../rpaSeoUtils";

export const HR_DOMAIN_INTRO = {
  title: "HR Domain",
  content: "Document AI + RPA and workflow automation across Recruitment, Onboarding, Employee Document Verification, Payroll Support, Attendance and Leave, Performance Management, Training, HR Analytics, HR Compliance, Offboarding, and more. Each use case includes challenge and solution detail, process diagrams, and human-in-the-loop review for enterprises in India, UAE, KSA, UK, and USA.",
  seoDescription: "HR Domain automation use cases — document AI + RPA and workflow automation across Recruitment, Onboarding, Employee Document Verification, Payroll Support, Attendance and Leave, Performance Management, Training, HR Analytics, HR Compliance, Offboarding, and more. Validation, approvals, system updates, and exception handling.",
};

const slugify = (label) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) {
  return {
    ...uc,
    groupId: slugify(uc.subIndustry),
    seoTitle: uc.title,
    title: uc.shortTitle || uc.title,
    systems: uc.systems || SYSTEMS,
  };
}

const SYSTEMS = "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, SharePoint, ITSM";

const RAW = [
  {
    id: "candidate-interview-scheduling",
    subIndustry: "Recruitment",
    department: "Talent Acquisition",
    businessProcess: "Candidate Interview Scheduling",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Candidate Interview Scheduling",
    title: "AI Candidate Interview Scheduling Automation for Recruitment",
    seoKeywords: "candidate interview scheduling automation, recruitment AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Talent Acquisition teams often manage candidate interview scheduling for Recruitment through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into back-and-forth emails for interview slots.",
    solution: "Deploy an RPA + Workflow Automation solution to coordinate candidate, panel and calendar availability automatically. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "pre-joining-task-tracker",
    subIndustry: "Onboarding",
    department: "HR Operations",
    businessProcess: "Pre-Joining Task Tracker",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Pre-Joining Task Tracker",
    title: "AI Pre-Joining Task Tracker Automation for Onboarding",
    seoKeywords: "pre-joining task tracker automation, onboarding AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Operations teams often manage pre-joining task tracker for Onboarding through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into joining tasks missed across HR, IT and admin teams.",
    solution: "Deploy an RPA + Workflow Automation solution to trigger tasks, reminders and status dashboards before joining date. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "High",
  },
  {
    id: "induction-schedule-assistant",
    subIndustry: "Onboarding",
    department: "Learning and Development",
    businessProcess: "Induction Schedule Assistant",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Induction Schedule Assistant",
    title: "AI Induction Schedule Assistant Automation for Onboarding",
    seoKeywords: "induction schedule assistant automation, onboarding AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Learning and Development teams often manage induction schedule assistant for Onboarding through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into induction sessions coordinated manually.",
    solution: "Deploy an RPA + Workflow Automation solution to assign sessions, send invites and track completion. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "background-verification-case-tracking",
    subIndustry: "Employee Document Verification",
    department: "HR Compliance",
    businessProcess: "Background Verification Case Tracking",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Background Verification Case Tracking",
    title: "AI Background Verification Case Tracking Automation for Employee Document Verification",
    seoKeywords: "background verification case tracking automation, employee document verification AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Compliance teams often manage background verification case tracking for Employee Document Verification through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into BGV status tracked in spreadsheets.",
    solution: "Deploy an RPA + Workflow Automation solution to update BGV status, reminders and exception queues. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "payroll-input-collection",
    subIndustry: "Payroll Support",
    department: "Payroll Operations",
    businessProcess: "Payroll Input Collection",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Payroll Input Collection",
    title: "AI Payroll Input Collection Automation for Payroll Support",
    seoKeywords: "payroll input collection automation, payroll support AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Payroll Operations teams often manage payroll input collection for Payroll Support through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into payroll inputs arrive in multiple formats.",
    solution: "Deploy an RPA + Workflow Automation solution to collect, validate and consolidate monthly payroll inputs. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "attendance-exception-regularization",
    subIndustry: "Attendance and Leave",
    department: "HR Operations",
    businessProcess: "Attendance Exception Regularization",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Attendance Exception Regularization",
    title: "AI Attendance Exception Regularization Automation for Attendance and Leave",
    seoKeywords: "attendance exception regularization automation, attendance and leave AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Operations teams often manage attendance exception regularization for Attendance and Leave through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into attendance gaps followed up manually.",
    solution: "Deploy an RPA + Workflow Automation solution to detect missing punches and trigger employee regularization requests. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "goal-setting-reminder-workflow",
    subIndustry: "Performance Management",
    department: "Performance Management",
    businessProcess: "Goal Setting Reminder Workflow",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Goal Setting Reminder Workflow",
    title: "AI Goal Setting Reminder Workflow Automation for Performance Management",
    seoKeywords: "goal setting reminder workflow automation, performance management AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Performance Management teams often manage goal setting reminder workflow for Performance Management through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into goal setting completion tracked manually.",
    solution: "Deploy an RPA + Workflow Automation solution to send reminders and dashboards for pending goal submissions. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "training-nomination-workflow",
    subIndustry: "Training",
    department: "Learning and Development",
    businessProcess: "Training Nomination Workflow",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Training Nomination Workflow",
    title: "AI Training Nomination Workflow Automation for Training",
    seoKeywords: "training nomination workflow automation, training AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Learning and Development teams often manage training nomination workflow for Training through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into training nominations tracked through emails.",
    solution: "Deploy an RPA + Workflow Automation solution to collect nominations, approvals and attendance automatically. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "certificate-generation-and-distribution",
    subIndustry: "Training",
    department: "Learning and Development",
    businessProcess: "Certificate Generation and Distribution",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Certificate Generation and Distribution",
    title: "AI Certificate Generation and Distribution Automation for Training",
    seoKeywords: "certificate generation and distribution automation, training AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Learning and Development teams often manage certificate generation and distribution for Training through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into certificates are created and emailed manually.",
    solution: "Deploy an RPA + Workflow Automation solution to generate certificates and send them to participants automatically. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Low",
  },
  {
    id: "headcount-reporting-automation",
    subIndustry: "HR Analytics",
    department: "HR Analytics",
    businessProcess: "Headcount Reporting Automation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Headcount Reporting Automation",
    title: "AI Headcount Reporting Automation Automation for HR Analytics",
    seoKeywords: "headcount reporting automation automation, hr analytics AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Analytics teams often manage headcount reporting automation for HR Analytics through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into headcount reports prepared manually.",
    solution: "Deploy an RPA + Workflow Automation solution to refresh headcount by department, location and employment type. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "hr-kpi-management-pack",
    subIndustry: "HR Analytics",
    department: "HR Analytics",
    businessProcess: "HR KPI Management Pack",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "HR KPI Management Pack",
    title: "AI HR KPI Management Pack Automation for HR Analytics",
    seoKeywords: "hr kpi management pack automation, hr analytics AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Analytics teams often manage hr kpi management pack for HR Analytics through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into HR KPIs prepared in spreadsheets.",
    solution: "Deploy an RPA + Workflow Automation solution to refresh recruitment, attrition, payroll and learning KPI pack. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "statutory-register-automation",
    subIndustry: "HR Compliance",
    department: "HR Compliance",
    businessProcess: "Statutory Register Automation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Statutory Register Automation",
    title: "AI Statutory Register Automation Automation for HR Compliance",
    seoKeywords: "statutory register automation automation, hr compliance AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Compliance teams often manage statutory register automation for HR Compliance through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into statutory registers prepared manually.",
    solution: "Deploy an RPA + Workflow Automation solution to prepare register data from HRMS and payroll records. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "policy-acknowledgement-tracker",
    subIndustry: "HR Compliance",
    department: "HR Compliance",
    businessProcess: "Policy Acknowledgement Tracker",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Policy Acknowledgement Tracker",
    title: "AI Policy Acknowledgement Tracker Automation for HR Compliance",
    seoKeywords: "policy acknowledgement tracker automation, hr compliance AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Compliance teams often manage policy acknowledgement tracker for HR Compliance through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into policy acknowledgements are chased manually.",
    solution: "Deploy an RPA + Workflow Automation solution to collect acknowledgements and report missing confirmations. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "exit-clearance-automation",
    subIndustry: "Offboarding",
    department: "HR Operations",
    businessProcess: "Exit Clearance Automation",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Exit Clearance Automation",
    title: "AI Exit Clearance Automation Automation for Offboarding",
    seoKeywords: "exit clearance automation automation, offboarding AI automation, hr domain RPA services, agentic AI solution",
    challenge: "HR Operations teams often manage exit clearance automation for Offboarding through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into clearance approvals are delayed across departments.",
    solution: "Deploy an RPA + Workflow Automation solution to route clearance tasks to IT, admin, finance and manager. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "recognition-nomination-workflow",
    subIndustry: "Employee Engagement",
    department: "Employee Engagement",
    businessProcess: "Recognition Nomination Workflow",
    regions: "India, UAE, KSA, UK, USA",
    shortTitle: "Recognition Nomination Workflow",
    title: "AI Recognition Nomination Workflow Automation for Employee Engagement",
    seoKeywords: "recognition nomination workflow automation, employee engagement AI automation, hr domain RPA services, agentic AI solution",
    challenge: "Employee Engagement teams often manage recognition nomination workflow for Employee Engagement through emails, spreadsheets, portals, and manual checks. This creates delays, inconsistent validation, rework, and limited visibility into recognition nominations are collected manually.",
    solution: "Deploy an RPA + Workflow Automation solution to collect nominations, validate eligibility and route approvals. The workflow should include business rules, human review for exceptions, status tracking, and audit evidence.",
    automationType: "RPA + Workflow Automation",
    buyerPersona: "CHRO, HR Head, Talent Acquisition Head, HR Operations Head, CIO",
    systems: "HRMS, Workday, SAP SuccessFactors, Darwinbox, Zoho People, Payroll System, Email, Calendar, SharePoint, ITSM",
    agentsRequired: "Bot Runner, Validation Agent, Exception Queue Agent",
    inputData: "Emails, forms, approval matrix, SOPs, transaction records, master data",
    outputActions: "System update, transaction posting, report generation, email notification",
    keyBenefits: "Reduced Repetitive Work, Fewer Copy-Paste Errors, Faster Processing",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
];
export const HR_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["Recruitment", "Onboarding", "Employee Document Verification", "Payroll Support", "Attendance and Leave", "Performance Management", "Training", "HR Analytics", "HR Compliance", "Offboarding", "Employee Engagement"];
export const HR_GROUPS = GROUP_ORDER.map((label) => ({
  id: slugify(label),
  label,
})).filter((g) => HR_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildRpaDomainSeoBundle({
  domainSlug: 'hr-domain',
  domainIntro: HR_DOMAIN_INTRO,
  useCases: HR_USE_CASES,
});
export const HR_USE_CASES_SEO = seo.useCasesSeo;
export const HR_DOMAIN_FAQS = seo.domainFaqs;
export const HR_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const HR_FAQ_JSONLD = seo.faqJsonLd;
export const HR_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const HR_WEBPAGE_JSONLD = seo.webPageJsonLd;

